import React, {useEffect, useMemo, useState} from "react";
import {
    Autocomplete,
    Box,
    Button,
    CardContent,
    Checkbox,
    FormControlLabel,
    Stack,
    TextField,
    Typography
} from "@mui/material";
import {useDispatch, useSelector} from "react-redux";
import PageLayout from "../components/layout/PageLayout";
import {useTranslation} from "react-i18next";
import {fetchCurrencies, selectCurrencies} from "../store/currencySlice";
import {
    fetchUserSettings,
    saveUserSettings,
    selectUserSettings,
    selectUserSettingsLoading
} from "../store/userSettingsSlice";
import {fetchWalletsAndGroups, selectActiveNonDebtWallets} from "../store/walletSlice";
import ColorPicker from "../components/input/ColorPicker";
import WalletIcon from "../components/layout/WalletIcon";
import {
    DEFAULT_PRIMARY_INDEX,
    DEFAULT_SECONDARY_INDEX,
    PRIMARY_COLORS,
    SECONDARY_COLORS
} from "../theme/themePalettes";

// Sorted-equality compare for two Long-id arrays. Used to detect dirty state
// for the wallet selection without depending on insertion order.
const sameIdSet = (a, b) => {
    const aa = (a || []).map(Number).sort((x, y) => x - y);
    const bb = (b || []).map(Number).sort((x, y) => x - y);
    if (aa.length !== bb.length) return false;
    return aa.every((id, idx) => id === bb[idx]);
};

const SettingsPage = () => {
    const {t} = useTranslation();
    const dispatch = useDispatch();
    const currencies = useSelector(selectCurrencies);
    const settings = useSelector(selectUserSettings);
    const isLoading = useSelector(selectUserSettingsLoading);
    const availableWallets = useSelector(selectActiveNonDebtWallets);

    const [defaultCurrencyId, setDefaultCurrencyId] = useState(null);
    const [primaryColorIndex, setPrimaryColorIndex] = useState(DEFAULT_PRIMARY_INDEX);
    const [secondaryColorIndex, setSecondaryColorIndex] = useState(DEFAULT_SECONDARY_INDEX);
    const [mainScreenWalletIds, setMainScreenWalletIds] = useState([]);
    const [statusMessage, setStatusMessage] = useState(null);
    const [statusError, setStatusError] = useState(null);
    const [isSaving, setIsSaving] = useState(false);

    useEffect(() => {
        dispatch(fetchCurrencies());
        dispatch(fetchUserSettings());
        dispatch(fetchWalletsAndGroups());
    }, [dispatch]);

    useEffect(() => {
        setDefaultCurrencyId(settings?.defaultCurrencyId ?? null);
        setPrimaryColorIndex(settings?.primaryColorIndex ?? DEFAULT_PRIMARY_INDEX);
        setSecondaryColorIndex(settings?.secondaryColorIndex ?? DEFAULT_SECONDARY_INDEX);
        // Intersect saved IDs with currently-existing non-DEBT wallets so the
        // checkbox state never references deleted wallets. The next save will
        // persist this cleaned-up list (= "self-healing" behaviour from the plan).
        const saved = (settings?.mainScreenWalletIds || []).map(Number);
        const availableIds = new Set((availableWallets || []).map((w) => Number(w.id)));
        setMainScreenWalletIds(saved.filter((id) => availableIds.has(id)));
    }, [settings, availableWallets]);

    const activeCurrencies = useMemo(() => {
        return (currencies || []).filter((currency) => currency.state !== "DELETED");
    }, [currencies]);

    const selectedCurrency = useMemo(() => {
        return activeCurrencies.find((currency) => currency.id === defaultCurrencyId) || null;
    }, [activeCurrencies, defaultCurrencyId]);

    const sortedWallets = useMemo(() => {
        return [...(availableWallets || [])].sort((a, b) =>
            (a?.name || "").localeCompare(b?.name || "", undefined, {sensitivity: "base"}));
    }, [availableWallets]);

    const toggleWallet = (walletId) => {
        const id = Number(walletId);
        setMainScreenWalletIds((prev) => {
            const has = prev.some((existing) => Number(existing) === id);
            return has ? prev.filter((existing) => Number(existing) !== id) : [...prev, id];
        });
    };

    const savedWalletIds = useMemo(() => {
        const saved = (settings?.mainScreenWalletIds || []).map(Number);
        const availableIds = new Set((availableWallets || []).map((w) => Number(w.id)));
        return saved.filter((id) => availableIds.has(id));
    }, [settings, availableWallets]);

    const isDirty =
        (settings?.defaultCurrencyId ?? null) !== defaultCurrencyId
        || (settings?.primaryColorIndex ?? DEFAULT_PRIMARY_INDEX) !== primaryColorIndex
        || (settings?.secondaryColorIndex ?? DEFAULT_SECONDARY_INDEX) !== secondaryColorIndex
        || !sameIdSet(savedWalletIds, mainScreenWalletIds);

    const handleSave = () => {
        setStatusError(null);
        setStatusMessage(null);
        setIsSaving(true);
        dispatch(saveUserSettings(
            {
                defaultCurrencyId: defaultCurrencyId,
                primaryColorIndex: primaryColorIndex,
                secondaryColorIndex: secondaryColorIndex,
                mainScreenWalletIds: mainScreenWalletIds
            },
            () => {
                setIsSaving(false);
                setStatusMessage(t("pages.settings.messages.saved"));
            },
            (error) => {
                setIsSaving(false);
                setStatusError(error?.message || t("pages.settings.messages.saveFailed"));
            }
        ));
    };

    return (
        <PageLayout title={t("pages.settings.title")}>
            <CardContent sx={{minHeight: 200, maxHeight: "82vh", overflowY: "auto"}}>
                <Box sx={{maxWidth: 480, m: "0 auto", display: "flex", flexDirection: "column", gap: 3}}>
                    <Box>
                        <Typography variant="h6" color="primary" sx={{mb: 1}}>
                            {t("pages.settings.sections.currencies")}
                        </Typography>
                        <Autocomplete
                            options={activeCurrencies}
                            value={selectedCurrency}
                            loading={isLoading}
                            onChange={(_event, value) => setDefaultCurrencyId(value ? value.id : null)}
                            getOptionLabel={(currency) => currency?.name || ""}
                            isOptionEqualToValue={(option, value) => option?.id === value?.id}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    variant="standard"
                                    label={t("pages.settings.defaultCurrency")}
                                    inputProps={{
                                        ...params.inputProps,
                                        autoComplete: "off"
                                    }}
                                />
                            )}
                        />
                    </Box>
                    <Box>
                        <Typography variant="h6" color="primary" sx={{mb: 1}}>
                            {t("pages.settings.sections.appearance")}
                        </Typography>
                        <Stack spacing={3}>
                            <ColorPicker
                                label={t("pages.settings.primaryColor")}
                                value={primaryColorIndex}
                                colors={PRIMARY_COLORS}
                                onChange={setPrimaryColorIndex}
                            />
                            <ColorPicker
                                label={t("pages.settings.secondaryColor")}
                                value={secondaryColorIndex}
                                colors={SECONDARY_COLORS}
                                onChange={setSecondaryColorIndex}
                            />
                        </Stack>
                    </Box>
                    <Box>
                        <Typography variant="h6" color="primary" sx={{mb: 1}}>
                            {t("pages.settings.sections.wallets")}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" sx={{display: "block", mb: 1}}>
                            {t("pages.settings.mainScreenWalletsHelp")}
                        </Typography>
                        <Box sx={{display: "flex", flexDirection: "column"}}>
                            {sortedWallets.map((wallet) => {
                                const id = Number(wallet.id);
                                const checked = mainScreenWalletIds.some((existing) => Number(existing) === id);
                                return (
                                    <FormControlLabel
                                        key={id}
                                        control={
                                            <Checkbox
                                                checked={checked}
                                                onChange={() => toggleWallet(id)}
                                            />
                                        }
                                        label={
                                            <Box sx={{display: "flex", alignItems: "center", gap: 1}}>
                                                <WalletIcon image={wallet.image}/>
                                                <span>{wallet.name}</span>
                                            </Box>
                                        }
                                    />
                                );
                            })}
                        </Box>
                    </Box>
                    <Stack direction="row" spacing={2}>
                        <Button
                            variant="contained"
                            disabled={!isDirty || isSaving}
                            onClick={handleSave}
                        >
                            {t("common.buttons.save")}
                        </Button>
                    </Stack>
                    {statusMessage && (
                        <Typography variant="body2" color="success.main">
                            {statusMessage}
                        </Typography>
                    )}
                    {statusError && (
                        <Typography variant="body2" color="error">
                            {statusError}
                        </Typography>
                    )}
                </Box>
            </CardContent>
        </PageLayout>
    );
};

export default SettingsPage;
